import React, { useMemo } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Legend, Tooltip
} from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { COLORS, COLORS_DARK, LANGUAGE_NAMES } from '../../constants';
import useResponsiveChart from '../../hooks/useResponsiveChart';
import CustomTooltip from '../CustomTooltip';
import ExplanationCard from '../ExplanationCard';

const RadarChartView = ({
  selectedMethod,
  selectedModel,
  methodData,
  selectedLanguages,
  chartData
}) => {
  const { isDarkMode } = useTheme();
  const colors = isDarkMode ? COLORS_DARK : COLORS;
  const { chartHeightSmall } = useResponsiveChart();

  // Prepare radar data - one point per language with performance metrics
  const radarData = useMemo(() => {
    if (!selectedMethod || !selectedModel || !methodData[selectedMethod]?.[selectedModel]) {
      return [];
    }

    const modelData = methodData[selectedMethod][selectedModel];

    return selectedLanguages.map(lang => {
      const langData = modelData[lang] || [];
      const validValues = langData.filter(v => v != null && !isNaN(v));

      if (validValues.length === 0) {
        return {
          language: lang,
          fullName: LANGUAGE_NAMES[lang] || lang,
          mean: 0,
          max: 0,
          min: 0,
          improvement: 0,
          consistency: 0
        };
      }

      const mean = validValues.reduce((a, b) => a + b, 0) / validValues.length;
      const max = Math.max(...validValues);
      const min = Math.min(...validValues);
      const improvement = max - validValues[0];
      const std = Math.sqrt(validValues.map(v => Math.pow(v - mean, 2)).reduce((a, b) => a + b, 0) / validValues.length);
      const consistency = 100 - std; // Higher is more consistent

      return {
        language: lang,
        fullName: LANGUAGE_NAMES[lang] || lang,
        mean: Math.round(mean * 10) / 10,
        max: Math.round(max * 10) / 10,
        min: Math.round(min * 10) / 10,
        improvement: Math.round(improvement * 10) / 10,
        consistency: Math.round(consistency * 10) / 10
      };
    });
  }, [selectedMethod, selectedModel, methodData, selectedLanguages]);

  // Prepare data for radar chart display (metrics as axes)
  const radarMetricsData = useMemo(() => {
    const metrics = ['Mean Score', 'Max Score', 'Min Score', 'Improvement', 'Consistency'];

    return metrics.map(metric => {
      const dataPoint = { metric };

      radarData.forEach(langData => {
        let value = 0;
        switch (metric) {
          case 'Mean Score': value = langData.mean; break;
          case 'Max Score': value = langData.max; break;
          case 'Min Score': value = langData.min; break;
          case 'Improvement': value = Math.max(0, langData.improvement + 20); break; // Offset for visibility
          case 'Consistency': value = langData.consistency; break;
          default: value = 0;
        }
        dataPoint[langData.language] = value;
      });

      return dataPoint;
    });
  }, [radarData]);

  return (
    <div className="mb-8">
      <ExplanationCard title="Radar Chart Explanation">
        <p><strong>Radar Chart:</strong> Shows multi-dimensional performance comparison across languages.</p>
        <p><strong>Each axis:</strong> Represents a performance metric (Mean, Max, Improvement, etc.).</p>
        <p><strong>Each colored area:</strong> Represents a language's performance profile.</p>
        {/* <p><strong>Interpretation:</strong> Larger areas indicate better overall performance. Shape shows strengths and weaknesses across metrics.</p> */}
      </ExplanationCard>

      <h3 className="text-3xl font-bold mb-3 text-center" style={{ color: 'var(--text-primary)' }}>
        Multi-dimensional Performance Analysis
      </h3>
      <p className="text-lg text-center mb-8" style={{ color: 'var(--text-secondary)' }}>
        {selectedMethod} method with {selectedModel} model
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Metrics as Axes */}
        <div className="rounded-xl p-6 shadow-lg border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
          <h4 className="text-lg font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
            Performance Metrics by Language
          </h4>
          <ResponsiveContainer width="100%" height={chartHeightSmall}>
            <RadarChart data={radarMetricsData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
              <PolarGrid stroke={isDarkMode ? '#333333' : '#e5e7eb'} />
              <PolarAngleAxis
                dataKey="metric"
                tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: 12 }}
              />
              <PolarRadiusAxis
                angle={30}
                domain={[0, 100]}
                tick={{ fill: isDarkMode ? '#737373' : '#6b7280', fontSize: 10 }}
              />
              {selectedLanguages.map(lang => (
                <Radar
                  key={lang}
                  name={lang}
                  dataKey={lang}
                  stroke={colors[lang]}
                  fill={colors[lang]}
                  fillOpacity={0.25}
                  strokeWidth={2}
                />
              ))}
              <Legend />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Languages as Axes (Average Performance) */}
        <div className="rounded-xl p-6 shadow-lg border" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
          <h4 className="text-lg font-semibold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
            Language Performance Comparison
          </h4>
          <ResponsiveContainer width="100%" height={chartHeightSmall}>
            <RadarChart data={radarData} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
              <PolarGrid stroke={isDarkMode ? '#333333' : '#e5e7eb'} />
              <PolarAngleAxis
                dataKey="fullName"
                tick={{ fill: isDarkMode ? '#a3a3a3' : '#4a5568', fontSize: 12 }}
              />
              <PolarRadiusAxis
                angle={30}
                domain={[0, 100]}
                tick={{ fill: isDarkMode ? '#737373' : '#6b7280', fontSize: 10 }}
              />
              <Radar
                name="Average Score"
                dataKey="mean"
                stroke={isDarkMode ? '#60a5fa' : '#3b82f6'}
                fill={isDarkMode ? '#60a5fa' : '#3b82f6'}
                fillOpacity={0.3}
                strokeWidth={2}
              />
              <Radar
                name="Max Score"
                dataKey="max"
                stroke={isDarkMode ? '#4ade80' : '#10b981'}
                fill={isDarkMode ? '#4ade80' : '#10b981'}
                fillOpacity={0.2}
                strokeWidth={2}
              />
              <Legend />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Summary Table */}
      <div className="mt-8 rounded-xl shadow-lg border overflow-hidden" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-primary)' }}>
        <h4 className="text-lg font-semibold p-4 text-center" style={{ color: 'var(--text-primary)', backgroundColor: 'var(--bg-secondary)' }}>
          Performance Summary
        </h4>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr style={{ backgroundColor: 'var(--bg-secondary)' }}>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Language</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Mean</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Max</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Min</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Improvement</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Consistency</th>
              </tr>
            </thead>
            <tbody>
              {radarData.map((row, idx) => (
                <tr key={row.language} style={{ backgroundColor: idx % 2 === 0 ? 'var(--bg-secondary)' : 'transparent' }}>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: colors[row.language] + '20', color: colors[row.language] }}
                    >
                      {row.fullName}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold" style={{ color: 'var(--text-primary)' }}>{row.mean}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--success-500)' }}>{row.max}</td>
                  <td className="px-4 py-3" style={{ color: 'var(--error-500)' }}>{row.min}</td>
                  <td className={`px-4 py-3 font-medium ${row.improvement >= 0 ? '' : ''}`}
                    style={{ color: row.improvement >= 0 ? 'var(--success-500)' : 'var(--error-500)' }}>
                    {row.improvement >= 0 ? '+' : ''}{row.improvement}
                  </td>
                  <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{row.consistency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RadarChartView;
